/**
 * Dynamic Route Generator
 *
 * Generates routes dynamically from:
 * 1. menuList (from backend API via permissionStore)
 * 2. componentRegistry (maps functionId → component)
 *
 * NO MORE HARDCODED ROUTES!
 * Just add to MFUN_TBL (database) + componentRegistry, routes auto-generate.
 */
import type { RouteConfig } from './routes';
import type { MenuFunctionItem } from '@/store/permissionStore';
import {
  getComponentForFunction,
  getTitleForFunction,
  getBreadcrumbForFunction,
} from './componentRegistry';
import { logger } from '@/shared/utils/logger';

/**
 * Build a RouteConfig from a single menu item.
 * Returns null when the item should be skipped.
 */
function buildRouteConfig(
  menu: MenuFunctionItem,
  menuList: MenuFunctionItem[],
): RouteConfig | null {
  if (!menu.functionId || !menu.path) {
    return null;
  }

  const component = getComponentForFunction(menu.functionId);
  if (!component) {
    return null;
  }

  const hasChildren = menuList.some(m => m.parentId === menu.nodeId);
  if (hasChildren && menu.parentId === null) {
    return null;
  }

  const routePath = menu.path === '/' ? '/' : menu.path.replace(/^\//, '');
  const isIndex = menu.path === '/';
  const title =
    getTitleForFunction(menu.functionId) ||
    menu.functionName ||
    menu.functionId;
  const breadcrumb = getBreadcrumbForFunction(menu.functionId) || [
    'Home',
    title,
  ];

  return {
    path: routePath,
    component,
    index: isIndex,
    meta: {
      title,
      requiresAuth: true,
      breadcrumb,
      functionId: menu.functionId,
    },
  };
}

/**
 * Generate protected routes from menuList
 *
 * @param menuList - Menu list from backend API (stored in permissionStore)
 * @returns Array of RouteConfig for protected routes
 *
 * @example
 * ```ts
 * // Backend returns menuList with:
 * // { nodeId: 64, functionId: 'USER', path: '/user', functionName: 'User Management' }
 *
 * // This generates:
 * // {
 * //   path: 'user',
 * //   component: UserManagement (from registry),
 * //   meta: { functionId: 'USER', title: 'User Management', ... }
 * // }
 * ```
 */
export const generateProtectedRoutes = (
  menuList: MenuFunctionItem[],
): RouteConfig[] => {
  const routes: RouteConfig[] = [];

  logger.debug('Generating dynamic routes from menuList', {
    menuCount: menuList.length,
  });

  for (const menu of menuList) {
    const route = buildRouteConfig(menu, menuList);
    if (!route) {
      continue;
    }

    routes.push(route);
    logger.debug('Generated route', {
      functionId: menu.functionId,
      path: route.path,
      title: route.meta?.title,
    });
  }

  logger.info('Dynamic routes generated', {
    totalMenuItems: menuList.length,
    generatedRoutes: routes.length,
    routes: routes.map(r => ({
      path: r.path,
      functionId: r.meta?.functionId,
    })),
  });

  return routes;
};

/**
 * Get protected routes for current user
 * Uses menuList from permissionStore
 *
 * @param menuList - Menu list from permissionStore
 * @returns Protected routes for authenticated user
 */
export const getProtectedRoutes = (
  menuList: MenuFunctionItem[],
): RouteConfig[] => {
  if (!menuList || menuList.length === 0) {
    logger.warn('No menuList available, returning empty routes');
    return [];
  }

  return generateProtectedRoutes(menuList);
};
